var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { localized, msg } from '@lit/localize';
import './BasicMap';
import './tool/ToolBox';
import '../button/SimpleButton';
import { LocateTool } from '../../model/tool/LocateTool';
import { NominatimSearchTool } from '../../model/tool/search/NominatimSearchTool';
import { Tool } from '../../model/tool/Tool';
import { ZoomInTool } from '../../model/tool/ZoomInTool';
import { ZoomOutTool } from '../../model/tool/ZoomOutTool';
const DEFAULT_PIN_COLOR = '#08c1ff';
const SOURCE_ID = 'editor-pois';
const LAYER_ID = 'editor-pois-layer';
/**
 * Same pin SVG as `PoiMap.buildPinSvg` — width/height/viewBox/paths kept
 * verbatim so the editor map renders an icon identical to the home and
 * network maps. Color and optional logo URL are interpolated.
 */
function buildPinSvg(color, logoUrl) {
    if (logoUrl) {
        return `
    <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 22.5 30">
      <path style="fill:${color};" d="M11.25,0C5.04,0,0,5.04,0,11.25c0,2.68.91,5.28,2.59,7.37l8.66,11.38,8.66-11.38c1.67-2.09,2.59-4.69,2.59-7.37C22.5,5.04,17.46,0,11.25,0Z"/>
      <circle style="fill:#fff;" cx="11.25" cy="11.25" r="8"/>
      <clipPath id="logoClip"><circle cx="11.25" cy="11.25" r="7"/></clipPath>
      <image href="${logoUrl}" x="4.25" y="4.25" width="16" height="16" clip-path="url(#logoClip)" preserveAspectRatio="xMidYMid slice"/>
    </svg>
    `;
    }
    return `
    <svg viewBox="0 0 16 16" width="100" height="100" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 0C4.686 0 2 2.686 2 6c0 1.43.486 2.816 1.379 3.932L8 16l4.621-6.068C13.514 8.816 14 7.43 14 6c0-3.314-2.686-6-6-6z" fill="${color}"/>
      <circle cx="8" cy="6" r="2" fill="#ffffff"/>
    </svg>
    `;
}
function iconIdFor(color, logoUrl) {
    // Stable key per (color, logoUrl) pair, sanitised for use as a map image id.
    const raw = `${color}|${logoUrl ?? ''}`;
    return `editor-pin-${raw.replace(/[^A-Za-z0-9]/g, '_')}`;
}
/**
 * Map surface for the scenario editor view. Wraps `<basic-map>`, ships the
 * standard right-stick tool box, and renders POIs via a symbol layer whose
 * icons are built with the same pin SVG as `PoiMap` (color + optional logo).
 * Children of the host are slotted on top of the map for floating UI.
 */
let ScenarioEditorMap = class ScenarioEditorMap extends LitElement {
    constructor() {
        super(...arguments);
        this.mapOptions = {};
        this.scenarioId = null;
        this.markers = [];
        this.draft = null;
        this.draftDraggable = false;
        this.draftClickEditable = false;
        /** What the draft pin represents — drives the icon inside the orange disc. */
        this.draftMode = 'add';
        this._map = null;
        this._mapLib = null;
        this._loadedIcons = new Set();
        this._layerListenersBound = false;
        this._activePopup = null;
        this._badgeMarkers = [];
        this._draftMarker = null;
        this._pendingFit = null;
        this._tools = [
            new LocateTool(),
            new NominatimSearchTool(),
            new ZoomInTool(),
            new ZoomOutTool()
        ];
        this._onMapLoaded = async (e) => {
            this._map = e.detail?.map ?? null;
            if (!this._map)
                return;
            // Match BasicMap's library choice so we use the same Popup / Marker class.
            if (this.mapOptions.mapboxApiKey) {
                this._mapLib = (await import('mapbox-gl')).default;
            }
            else {
                this._mapLib = await import('maplibre-gl');
            }
            this._bindMapClickForDraft();
            this._syncDraft();
            this._sync();
            // Run any fit request that arrived before the map was ready.
            if (this._pendingFit) {
                const { coords, options } = this._pendingFit;
                this._pendingFit = null;
                this._applyFit(coords, options);
            }
        };
    }
    /**
     * Pan/zoom the map to fit the given coordinates. Defers until the map
     * instance is ready if called early. Default `maxZoom` is 14 so a single
     * point doesn't blow up to street level.
     */
    fitToCoordinates(coords, options) {
        if (!coords?.length)
            return;
        if (!this._map) {
            this._pendingFit = options ? { coords, options } : { coords };
            return;
        }
        this._applyFit(coords, options);
    }
    _applyFit(coords, options) {
        if (!this._map)
            return;
        const padding = options?.padding ?? 60;
        const maxZoom = options?.maxZoom ?? 13;
        const animate = options?.animate ?? true;
        // Drop garbage entries.
        const valid = coords.filter(([lng, lat]) => typeof lng === 'number' && typeof lat === 'number');
        if (valid.length === 0)
            return;
        // Single point: fitBounds on an empty bbox zooms past `maxZoom` in some
        // libraries — just fly there at the cap.
        if (valid.length === 1) {
            const first = valid[0];
            this._map.flyTo({
                center: [first[0], first[1]],
                zoom: maxZoom,
                animate
            });
            return;
        }
        let minLng = Infinity;
        let minLat = Infinity;
        let maxLng = -Infinity;
        let maxLat = -Infinity;
        for (const [lng, lat] of valid) {
            if (lng < minLng)
                minLng = lng;
            if (lat < minLat)
                minLat = lat;
            if (lng > maxLng)
                maxLng = lng;
            if (lat > maxLat)
                maxLat = lat;
        }
        this._map.fitBounds([
            [minLng, minLat],
            [maxLng, maxLat]
        ], { padding, maxZoom, animate });
    }
    _bindMapClickForDraft() {
        if (!this._map)
            return;
        this._map.on('click', (e) => {
            if (!this.draftClickEditable)
                return;
            const hits = this._map.queryRenderedFeatures(e.point, {
                layers: [LAYER_ID]
            });
            if (hits && hits.length > 0)
                return; // hit a POI pin → ignore
            this._emitDraftMove(e.lngLat.lng, e.lngLat.lat);
        });
    }
    _draftBadgeSvg(mode) {
        const inner = mode === 'edit'
            ? `<path fill="#ffffff" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" transform="translate(4 4) scale(0.66)"/>`
            : `<line x1="16" y1="9" x2="16" y2="23" stroke="#ffffff" stroke-width="3.5" stroke-linecap="round"/>
           <line x1="9" y1="16" x2="23" y2="16" stroke="#ffffff" stroke-width="3.5" stroke-linecap="round"/>`;
        return `
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"
        style="filter: drop-shadow(0 2px 3px rgba(0,0,0,0.3));">
        <circle cx="16" cy="16" r="14" fill="#f59e0b" stroke="#ffffff" stroke-width="2"/>
        ${inner}
      </svg>`;
    }
    _emitDraftMove(lng, lat) {
        this.dispatchEvent(new CustomEvent('draft-move', {
            detail: { lng, lat },
            bubbles: true,
            composed: true
        }));
    }
    _syncDraft() {
        if (!this._map || !this._mapLib?.Marker)
            return;
        const Marker = this._mapLib.Marker;
        if (!this.draft) {
            // Remove any existing draft pin.
            try {
                this._draftMarker?.remove?.();
            }
            catch {
                // ignore
            }
            this._draftMarker = null;
            return;
        }
        if (!this._draftMarker) {
            const el = document.createElement('div');
            el.style.cssText =
                'pointer-events:auto;cursor:grab;width:32px;height:32px;display:flex;align-items:center;justify-content:center;';
            el.innerHTML = this._draftBadgeSvg(this.draftMode);
            this._draftMarker = new Marker({
                element: el,
                anchor: 'center',
                draggable: this.draftDraggable
            })
                .setLngLat([this.draft.lng, this.draft.lat])
                .addTo(this._map);
            this._draftMarker.on?.('dragend', () => {
                const ll = this._draftMarker.getLngLat?.();
                if (ll)
                    this._emitDraftMove(ll.lng, ll.lat);
            });
        }
        else {
            this._draftMarker.setLngLat([this.draft.lng, this.draft.lat]);
            // Rebuild the marker if the draggable mode changed.
            const wasDraggable = !!this._draftMarker.isDraggable?.();
            if (wasDraggable !== this.draftDraggable) {
                try {
                    this._draftMarker.remove?.();
                }
                catch {
                    // ignore
                }
                this._draftMarker = null;
                this._syncDraft();
            }
        }
    }
    updated(changed) {
        if (changed.has('markers') && this._map)
            this._sync();
        if ((changed.has('draft') ||
            changed.has('draftDraggable') ||
            changed.has('draftClickEditable') ||
            changed.has('draftMode')) &&
            this._map &&
            this._mapLib) {
            this._syncDraft();
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        try {
            this._activePopup?.remove?.();
        }
        catch {
            // ignore
        }
        this._activePopup = null;
        this._clearBadges();
        try {
            this._draftMarker?.remove?.();
        }
        catch {
            // ignore
        }
        this._draftMarker = null;
    }
    _loadIcon(id, color, logoUrl) {
        return new Promise((resolve) => {
            if (this._map.hasImage(id)) {
                this._loadedIcons.add(id);
                resolve();
                return;
            }
            const svg = buildPinSvg(color, logoUrl);
            const img = new Image();
            img.onload = () => {
                if (!this._map.hasImage(id)) {
                    this._map.addImage(id, img, { sdf: false });
                }
                this._loadedIcons.add(id);
                resolve();
            };
            img.onerror = () => resolve();
            img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
        });
    }
    async _ensureIcons() {
        const unique = new Map();
        for (const m of this.markers) {
            const color = m.color ?? DEFAULT_PIN_COLOR;
            const logoUrl = m.logoUrl ?? null;
            const id = iconIdFor(color, logoUrl);
            if (!unique.has(id))
                unique.set(id, { color, logoUrl });
        }
        await Promise.all([...unique.entries()].map(([id, { color, logoUrl }]) => this._loadIcon(id, color, logoUrl)));
    }
    _toFeatureCollection() {
        return {
            type: 'FeatureCollection',
            features: this.markers
                .filter((m) => m.poi.longitude != null && m.poi.latitude != null)
                .map((m) => {
                const color = m.color ?? DEFAULT_PIN_COLOR;
                const logoUrl = m.logoUrl ?? null;
                return {
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [m.poi.longitude, m.poi.latitude]
                    },
                    properties: {
                        iconId: iconIdFor(color, logoUrl),
                        name: m.poi.name,
                        poiId: m.poi.id
                    }
                };
            })
        };
    }
    async _sync() {
        if (!this._map)
            return;
        const apply = async () => {
            await this._ensureIcons();
            const data = this._toFeatureCollection();
            const existing = this._map.getSource(SOURCE_ID);
            if (existing) {
                existing.setData(data);
            }
            else {
                this._map.addSource(SOURCE_ID, { type: 'geojson', data });
                this._map.addLayer({
                    id: LAYER_ID,
                    type: 'symbol',
                    source: SOURCE_ID,
                    layout: {
                        'icon-image': ['get', 'iconId'],
                        'icon-size': 0.3,
                        'icon-anchor': 'bottom',
                        'icon-allow-overlap': true
                    }
                });
                this._bindLayerListeners();
            }
        };
        if (this._map.isStyleLoaded?.()) {
            apply().then(() => this._renderBadges());
        }
        else {
            this._map.once?.('load', () => apply().then(() => this._renderBadges()));
        }
    }
    _clearBadges() {
        for (const m of this._badgeMarkers) {
            try {
                m.remove?.();
            }
            catch {
                // ignore
            }
        }
        this._badgeMarkers = [];
    }
    _renderBadges() {
        this._clearBadges();
        const Marker = this._mapLib?.Marker ?? this._mapLib?.default?.Marker;
        if (!Marker)
            return;
        for (const m of this.markers) {
            const ct = m.changeType;
            if (ct !== 'delete' && ct !== 'add' && ct !== 'edit' && ct !== 'transfer')
                continue;
            if (m.poi.longitude == null || m.poi.latitude == null)
                continue;
            let fill = '#dc3545';
            let inner = '<line x1="5" y1="5" x2="11" y2="11" stroke="#ffffff" stroke-width="2.4" stroke-linecap="round"/>' +
                '<line x1="11" y1="5" x2="5" y2="11" stroke="#ffffff" stroke-width="2.4" stroke-linecap="round"/>';
            if (ct === 'add') {
                fill = '#16a34a';
                inner =
                    '<line x1="8" y1="4" x2="8" y2="12" stroke="#ffffff" stroke-width="2.4" stroke-linecap="round"/>' +
                        '<line x1="4" y1="8" x2="12" y2="8" stroke="#ffffff" stroke-width="2.4" stroke-linecap="round"/>';
            }
            else if (ct === 'edit') {
                fill = '#0078d4';
                // small pencil glyph
                inner =
                    '<path d="M5 11 L9.5 6.5 L10.5 7.5 L6 12 Z" fill="#ffffff"/>' +
                        '<path d="M9.5 6.5 L10.5 5.5 L11.5 6.5 L10.5 7.5 Z" fill="#ffffff"/>';
            }
            else if (ct === 'transfer') {
                fill = '#f59e0b';
                // double-arrow transfer glyph (scaled to 16x16 viewBox)
                inner =
                    '<path fill="none" stroke="#ffffff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"' +
                        ' d="M3 6h7M9 4l2 2-2 2M13 10H6M8 8l-2 2 2 2"/>';
            }
            const el = document.createElement('div');
            el.style.cssText = [
                'width:16px',
                'height:16px',
                'pointer-events:none',
                'filter:drop-shadow(0 1px 1px rgba(0,0,0,0.25))'
            ].join(';');
            el.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
          <circle cx="8" cy="8" r="7" fill="${fill}" stroke="#ffffff" stroke-width="1.5"/>
          ${inner}
        </svg>`;
            // Pin renders ~30px tall with anchor at the bottom (lng/lat).
            // Place the badge near the top-right of the pin head.
            const marker = new Marker({ element: el, offset: [9, -24] })
                .setLngLat([m.poi.longitude, m.poi.latitude])
                .addTo(this._map);
            this._badgeMarkers.push(marker);
        }
    }
    _bindLayerListeners() {
        if (this._layerListenersBound || !this._map)
            return;
        this._layerListenersBound = true;
        // Pointer cursor + right-click context menu on POI pins.
        this._map.on('mouseenter', LAYER_ID, () => {
            this._map.getCanvas().style.cursor = 'pointer';
        });
        this._map.on('mouseleave', LAYER_ID, () => {
            this._map.getCanvas().style.cursor = '';
        });
        this._map.on('contextmenu', LAYER_ID, (e) => {
            e.preventDefault?.();
            const feature = e.features?.[0];
            if (!feature)
                return;
            const poiId = feature.properties?.poiId;
            const poi = this.markers.find((m) => m.poi.id === poiId)?.poi;
            if (!poi)
                return;
            this._openPoiMenu(e.lngLat, poi);
        });
        // Right-click on empty map space → "Add Poi" menu.
        this._map.on('contextmenu', (e) => {
            const hits = this._map.queryRenderedFeatures(e.point, {
                layers: [LAYER_ID]
            });
            if (hits && hits.length > 0)
                return; // a POI handled it
            e.preventDefault?.();
            this._openEmptyMenu(e.lngLat);
        });
    }
    _openEmptyMenu(lngLat) {
        if (!this._mapLib?.Popup)
            return;
        this._activePopup?.remove?.();
        const container = document.createElement('div');
        container.style.cssText = 'display:flex;flex-direction:column;min-width:140px;';
        const addBtn = document.createElement('simple-button');
        addBtn.label = msg('+ Add Poi', { id: 'scenarioEditor.addPoi', desc: 'Context menu button to add a POI' });
        addBtn.variant = 'primary';
        addBtn.size = 'small';
        addBtn.fluid = true;
        addBtn.addEventListener('button-click', () => {
            this._activePopup?.remove?.();
            this.dispatchEvent(new CustomEvent('add-poi-at', {
                detail: { lng: lngLat.lng, lat: lngLat.lat },
                bubbles: true,
                composed: true
            }));
        });
        container.appendChild(addBtn);
        this._activePopup = new this._mapLib.Popup({
            closeButton: false,
            closeOnClick: true,
            closeOnMove: true,
            offset: 6
        })
            .setLngLat([lngLat.lng, lngLat.lat])
            .setDOMContent(container)
            .addTo(this._map);
    }
    _openPoiMenu(lngLat, poi) {
        if (!this._mapLib?.Popup)
            return;
        this._activePopup?.remove?.();
        const container = document.createElement('div');
        container.className = 'scenario-editor-poi-menu';
        container.style.cssText =
            'display:flex;flex-direction:column;gap:6px;min-width:140px;';
        const editBtn = document.createElement('simple-button');
        editBtn.label = msg('Edit Poi', { id: 'scenarioEditor.editPoi', desc: 'Context menu button to edit a POI' });
        editBtn.variant = 'secondary';
        editBtn.size = 'small';
        editBtn.fluid = true;
        editBtn.addEventListener('button-click', () => {
            this._activePopup?.remove?.();
            this.dispatchEvent(new CustomEvent('edit-poi', {
                detail: { poi },
                bubbles: true,
                composed: true
            }));
        });
        const transfertBtn = document.createElement('simple-button');
        transfertBtn.label = msg('Transfer Poi', { id: 'scenarioEditor.transferPoi', desc: 'Context menu button to transfer a POI' });
        transfertBtn.variant = 'warning';
        transfertBtn.size = 'small';
        transfertBtn.fluid = true;
        transfertBtn.addEventListener('button-click', () => {
            this._activePopup?.remove?.();
            this.dispatchEvent(new CustomEvent('transfert-poi', {
                detail: { poi },
                bubbles: true,
                composed: true
            }));
        });
        const deleteBtn = document.createElement('simple-button');
        deleteBtn.label = msg('Delete Poi', { id: 'scenarioEditor.deletePoi', desc: 'Context menu button to delete a POI' });
        deleteBtn.variant = 'danger';
        deleteBtn.size = 'small';
        deleteBtn.fluid = true;
        deleteBtn.addEventListener('button-click', () => {
            this._activePopup?.remove?.();
            this.dispatchEvent(new CustomEvent('delete-poi', {
                detail: { poi },
                bubbles: true,
                composed: true
            }));
        });
        container.appendChild(editBtn);
        container.appendChild(transfertBtn);
        container.appendChild(deleteBtn);
        this._activePopup = new this._mapLib.Popup({
            closeButton: false,
            closeOnClick: true,
            closeOnMove: true,
            offset: 12
        })
            .setLngLat([lngLat.lng, lngLat.lat])
            .setDOMContent(container)
            .addTo(this._map);
    }
    render() {
        return html `
      <basic-map .mapOptions=${this.mapOptions} @map-loaded=${this._onMapLoaded}>
        <map-tool-box stick="right" .tools=${this._tools}></map-tool-box>
      </basic-map>
      <div class="overlay">
        <slot></slot>
      </div>
    `;
    }
};
ScenarioEditorMap.styles = css `
    :host {
      display: block;
      width: 100%;
      height: 100%;
      position: relative;
    }
    basic-map {
      width: 100%;
      height: 100%;
    }
    .overlay {
      position: absolute;
      inset: 0;
      pointer-events: none;
    }
    .overlay ::slotted(*) {
      pointer-events: auto;
    }
  `;
__decorate([
    property({ type: Object })
], ScenarioEditorMap.prototype, "mapOptions", void 0);
__decorate([
    property({ type: Number })
], ScenarioEditorMap.prototype, "scenarioId", void 0);
__decorate([
    property({ type: Array })
], ScenarioEditorMap.prototype, "markers", void 0);
__decorate([
    property({ type: Object })
], ScenarioEditorMap.prototype, "draft", void 0);
__decorate([
    property({ type: Boolean })
], ScenarioEditorMap.prototype, "draftDraggable", void 0);
__decorate([
    property({ type: Boolean })
], ScenarioEditorMap.prototype, "draftClickEditable", void 0);
__decorate([
    property({ type: String })
], ScenarioEditorMap.prototype, "draftMode", void 0);
__decorate([
    state()
], ScenarioEditorMap.prototype, "_map", void 0);
ScenarioEditorMap = __decorate([
    localized(),
    customElement('scenario-editor-map')
], ScenarioEditorMap);
export { ScenarioEditorMap };
//# sourceMappingURL=ScenarioEditorMap.js.map